import { ARTICLES } from '@/content/articles.mjs'
import { HOME_PAGE, INDEX_ROBOTS, LEGAL_ROBOTS } from '@/content/seoPages.mjs'

export const SITE_ORIGIN = 'https://www.statbox.co'
export const OG_IMAGE_URL = `${SITE_ORIGIN}/og-image.jpg`

export const INDEXABLE_ROBOTS = INDEX_ROBOTS
export const NOINDEX_ROBOTS = 'noindex, nofollow'

/** Crawlable but kept out of results (and out of sitelinks). */
export const LEGAL_PATHS = new Set(['/privacy', '/terms'])

/** Public URLs search engines should index. Everything else is noindex. */
export const INDEXABLE_PATHS = new Set([
  '/',
  '/book-demo',
  '/app-download',
  '/docs/api',
  '/docs/api.md',
  '/docs/api.html',
  '/llms.txt',
  '/sports/football',
  '/sports/basketball',
  '/sports/padel',
  '/solutions/communities',
  '/solutions/academies',
  '/solutions/venues',
  '/blog',
  ...ARTICLES.map((article) => `/blog/${article.slug}`),
])

export const HOME_SEO = {
  title: HOME_PAGE.title,
  description: HOME_PAGE.description,
  ogTitle: HOME_PAGE.ogTitle,
  ogDescription: HOME_PAGE.ogDescription,
  path: '/',
} as const

type PageMeta = {
  title: string
  description: string
  path: string
  ogTitle?: string
  ogDescription?: string
}

function canonicalUrl(path: string) {
  if (!path || path === '/') return `${SITE_ORIGIN}/`
  return `${SITE_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  const selector =
    attr === 'property' ? `meta[property="${key}"]` : `meta[name="${key}"]`
  let tag = document.querySelector(selector)
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attr, key)
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', content)
}

export function isIndexablePath(pathname: string) {
  const path = pathname.replace(/\/+$/, '') || '/'
  return INDEXABLE_PATHS.has(path)
}

export function robotsForPath(pathname: string) {
  const path = pathname.replace(/\/+$/, '') || '/'
  if (LEGAL_PATHS.has(path)) return LEGAL_ROBOTS
  return INDEXABLE_PATHS.has(path) ? INDEXABLE_ROBOTS : NOINDEX_ROBOTS
}

export function setRobots(indexable: boolean | string) {
  const content =
    typeof indexable === 'string' ? indexable : indexable ? INDEXABLE_ROBOTS : NOINDEX_ROBOTS
  upsertMeta('name', 'robots', content)
  upsertMeta('name', 'googlebot', content)
}

/** Update title, description, canonical, and social tags. Does not touch robots. */
export function applyPageMeta({
  title,
  description,
  path,
  ogTitle,
  ogDescription,
}: PageMeta) {
  const url = canonicalUrl(path)
  const socialTitle = ogTitle || title
  const socialDescription = ogDescription || description

  document.title = title
  upsertMeta('name', 'description', description)

  upsertMeta('property', 'og:title', socialTitle)
  upsertMeta('property', 'og:description', socialDescription)
  upsertMeta('property', 'og:image', OG_IMAGE_URL)
  upsertMeta('property', 'og:image:width', '1200')
  upsertMeta('property', 'og:image:height', '630')
  upsertMeta('property', 'og:url', url)
  upsertMeta('property', 'og:type', 'website')
  upsertMeta('property', 'og:site_name', 'Statbox')

  upsertMeta('name', 'twitter:card', 'summary_large_image')
  upsertMeta('name', 'twitter:title', socialTitle)
  upsertMeta('name', 'twitter:description', socialDescription)
  upsertMeta('name', 'twitter:image', OG_IMAGE_URL)

  let canonical = document.querySelector('link[rel="canonical"]')
  if (!canonical) {
    canonical = document.createElement('link')
    canonical.setAttribute('rel', 'canonical')
    document.head.appendChild(canonical)
  }
  canonical.setAttribute('href', url)

  const alternateTypes = ['text/markdown', 'text/html', 'text/plain']
  if (path !== '/') {
    alternateTypes.forEach((type) => {
      document.querySelector(`link[rel="alternate"][type="${type}"]`)?.remove()
    })
    return
  }

  const alternates = [
    { type: 'text/markdown', href: `${SITE_ORIGIN}/docs/api.md` },
    { type: 'text/html', href: `${SITE_ORIGIN}/docs/api.html` },
    { type: 'text/plain', href: `${SITE_ORIGIN}/llms.txt` },
  ]
  alternates.forEach(({ type, href }) => {
    let link = document.querySelector(`link[rel="alternate"][type="${type}"]`)
    if (!link) {
      link = document.createElement('link')
      link.setAttribute('rel', 'alternate')
      link.setAttribute('type', type)
      document.head.appendChild(link)
    }
    link.setAttribute('href', href)
  })
}
